'use client';

/**
 * @fileoverview `useGlobalSearchShortcut` (FRE-825) — site-wide keyboard
 * shortcut that opens the global `SearchSheet` overlay on ⌘K / Ctrl+K
 * (anywhere) and `/` (when no editable surface is focused).
 *
 * The hook is mounted once per top-level chrome surface — `AppShellLayout`
 * for the consumer app, `SiteHeader` for the marketing / apex surfaces — so
 * the shortcut works from every page that renders either surface. Both
 * surfaces talk to the same module-scoped `useSearchSheet` store, so a
 * second mount is harmless: opening an already-open sheet is a no-op
 * (`useSearchSheet`'s `setOpenImpl` short-circuits identical state).
 *
 * **Trigger source.** The hook stamps `{ trigger: 'kbd' | 'slash' }` on
 * `setOpen(true, …)` so the FRE-822 SearchSheet telemetry can record the
 * correct origin on the `search_opened` PostHog event. `'kbd'` covers
 * ⌘K / Ctrl+K; `'slash'` covers the `/` quick-open.
 *
 * **Editable-surface guard.** `/` is the most common letter in typing; the
 * hook never fires `/` when the user is typing into an `<input>`,
 * `<textarea>`, `<select>`, or `contenteditable` element. ⌘K is a standard
 * "open command palette" shortcut and intentionally fires even from inside
 * editable surfaces, matching macOS / Linear / GitHub conventions.
 *
 * **Platform.** ⌘K matches the macOS `metaKey`; everywhere else matches
 * `ctrlKey`. Detected at handler time via `navigator.platform`; the listener
 * itself is registered unconditionally so SSR + client agree.
 *
 * **SSR safety.** All `window` / `document` / `navigator` access is gated
 * behind the `useEffect` boundary so the hook is server-render-safe.
 *
 * **Page-local `/` override (FRE-1241).** A page may claim `/` for itself via
 * `registerSlashShortcutInterceptor` (e.g. the Discover query bar focuses in
 * place instead of opening Spotlight). This hook remains the single owner of
 * the `window` keydown listener — interceptors are consulted, not raced
 * against a second listener.
 *
 * @since FRE-825 (Global Nav Completion — M1)
 * @since FRE-1241 — page-local `/` interceptor registry
 */

import * as React from 'react';

import { useSearchSheet } from './useSearchSheet';

/**
 * Return `true` when the active focus is inside an editable surface where
 * intercepting a single character (e.g. `/`) would steal a keystroke.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  // jsdom does not always reflect `isContentEditable` through the attribute,
  // so fall back to a direct attribute check before the tag-name bail-out.
  const editable = target.getAttribute('contenteditable');
  if (editable === '' || editable === 'true' || editable === 'plaintext-only') {
    return true;
  }
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * `true` when the current platform reports as macOS (or iOS — for completeness
 * with hardware keyboards on iPad). Used to choose between `metaKey` (⌘) and
 * `ctrlKey`. Lazily evaluated each handler call so SSR never touches
 * `navigator`.
 */
function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform ?? '';
  return /Mac|iPod|iPhone|iPad/.test(platform);
}

/**
 * `true` when the keyboard event matches the platform-appropriate Cmd-K /
 * Ctrl-K combo. `event.key` is compared case-insensitively so ⌘+Shift+K
 * (which fires `'K'`) still matches the shortcut.
 *
 * **Malformed events (WEB-820).** The listener runs on `window` in the capture
 * phase, so it sees every `keydown` on the page — including synthetic ones
 * dispatched by password managers, autofill, and extensions. Those are not
 * always real `KeyboardEvent`s; a bare `new Event('keydown')` has no `key` at
 * all. Narrow the type before calling a string method, or an unguarded
 * `.toLowerCase()` throws an uncaught `TypeError` and aborts the listener for
 * that keypress. `matchesSlash` below is already safe by construction (it
 * compares `event.key` rather than calling a method on it).
 */
function matchesCmdK(event: KeyboardEvent): boolean {
  if (typeof event.key !== 'string') return false;
  if (event.key.toLowerCase() !== 'k') return false;
  // Standard cross-platform mod check: ⌘ on Mac, Ctrl elsewhere. We use
  // `metaKey || ctrlKey` (rather than a hard platform branch) so an external
  // keyboard on Mac that sends Ctrl+K still triggers — matches the WebKit /
  // Chromium command-palette convention.
  return event.metaKey || event.ctrlKey;
}

/**
 * `true` when the keyboard event is a bare `/` press — no modifiers, no
 * IME composition. Modifier-key combos (e.g. Cmd+/) are ignored so platform
 * "find on page" shortcuts keep working.
 */
function matchesSlash(event: KeyboardEvent): boolean {
  if (event.key !== '/') return false;
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return false;
  return true;
}

/* ------------------------------------------------------------------ */
/*  Page-local `/` interceptor registry (FRE-1241)                      */
/* ------------------------------------------------------------------ */

/**
 * A page-local override for the bare `/` quick-open. Return `true` to claim
 * the keypress (e.g. focus an in-page field) and suppress the global
 * `SearchSheet`; return `false` to defer to it. By the time interceptors are
 * consulted, this hook has already ruled out editable-surface focus and IME
 * composition, so an interceptor only needs to decide whether *its* surface
 * wants the key.
 */
export type SlashShortcutInterceptor = () => boolean;

const slashShortcutInterceptors = new Set<SlashShortcutInterceptor>();

/**
 * Register a page-local `/` interceptor that gets first refusal on the bare
 * `/` quick-open before the global `SearchSheet` opens. Interceptors run in
 * registration order; the first to return `true` wins. Returns an unsubscribe
 * function — call it on unmount.
 *
 * Used by the Discover surface (FRE-1241): on `/discover`, `/` should focus
 * the in-page query bar instead of opening Spotlight, while every other
 * surface keeps the existing `/`-opens-Spotlight behavior.
 */
export function registerSlashShortcutInterceptor(
  interceptor: SlashShortcutInterceptor,
): () => void {
  slashShortcutInterceptors.add(interceptor);
  return () => {
    slashShortcutInterceptors.delete(interceptor);
  };
}

/**
 * Run the registered interceptors in insertion order, stopping at the first
 * that claims the keypress. Snapshotting the set before iterating keeps a
 * self-unregistering interceptor from skipping a sibling — skipped entirely
 * on the (common, e.g. every non-Discover page) empty-registry fast path.
 */
function runSlashShortcutInterceptors(): boolean {
  if (slashShortcutInterceptors.size === 0) return false;
  for (const interceptor of [...slashShortcutInterceptors]) {
    if (interceptor()) return true;
  }
  return false;
}

export interface UseGlobalSearchShortcutOptions {
  /** Pause the listener without unmounting. Defaults to `true`. */
  readonly enabled?: boolean;
}

/**
 * Register the global ⌘K / Ctrl+K + `/` shortcuts. Mount once per top-level
 * chrome surface (`AppShellLayout`, `SiteHeader`).
 *
 * @example
 * ```tsx
 * // In AppShellLayout (consumer surface):
 * useGlobalSearchShortcut();
 *
 * // In SiteHeader (marketing / apex surface):
 * useGlobalSearchShortcut();
 * ```
 */
export function useGlobalSearchShortcut(options?: UseGlobalSearchShortcutOptions): void {
  const enabled = options?.enabled ?? true;
  const { setOpen } = useSearchSheet();

  React.useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const listener = (event: KeyboardEvent): void => {
      // IME composition — never intercept while the user is mid-input on a
      // composing surface (CJK, emoji picker, etc.).
      if (event.isComposing) return;

      if (matchesCmdK(event)) {
        // ⌘K / Ctrl+K — fires even from inside editable surfaces, matching
        // command-palette convention (Linear, GitHub, VSCode, etc.). Browser
        // defaults vary by platform (Chrome opens the omnibox, Firefox the
        // search field); preventDefault keeps the SearchSheet authoritative
        // on app surfaces.
        event.preventDefault();
        setOpen(true, { trigger: 'kbd' });
        return;
      }

      if (matchesSlash(event)) {
        if (isEditableTarget(event.target)) return;
        // Suppress the browser default (Firefox quick-find) regardless of who
        // handles the key. Give any page-local interceptor first refusal —
        // the Discover surface focuses its own query bar instead of opening
        // the global sheet (FRE-1241). Only fall back to the sheet when no
        // interceptor claimed the keypress.
        event.preventDefault();
        if (runSlashShortcutInterceptors()) return;
        setOpen(true, { trigger: 'slash' });
      }
    };

    // `window` + capture phase so we run BEFORE any descendant listener that
    // calls `stopPropagation()` on the bubble phase — without this, a focused
    // input that swallows ⌘K would block the global shortcut. The listener
    // is non-passive because we call `preventDefault()`. The capture-phase
    // option must be passed to BOTH add/remove or the listener leaks.
    window.addEventListener('keydown', listener, { capture: true });
    return () => {
      window.removeEventListener('keydown', listener, { capture: true });
    };
  }, [enabled, setOpen]);
}

/**
 * Detect whether the current platform should display the ⌘ glyph (Mac) or
 * the textual `Ctrl` label. Re-export of the internal helper so kbd-hint
 * components in `AppHeader` / `SiteHeader` can render the right glyph
 * without re-implementing platform detection.
 *
 * SSR-safe: returns `false` (the `Ctrl` default) on the server.
 */
export function useIsMacPlatform(): boolean {
  // Default to `false` so SSR markup is consistent. A post-mount effect
  // swaps to the real platform value; the kbd hint is small enough that a
  // one-character flicker is imperceptible (matches the
  // `KbdSearchHint` pattern already established in `SiteHeader.tsx`).
  const [isMac, setIsMac] = React.useState(false);
  React.useEffect(() => {
    setIsMac(isMacPlatform());
  }, []);
  return isMac;
}
